// AuthContext.js
import { createContext, useState, useEffect, useCallback } from 'react';
import Cookies from 'js-cookie';
import axios from 'axios';
import { auth } from 'lib/firebaseClient';
import { onAuthStateChanged, signOut } from 'firebase/auth';

export const AuthContext = createContext();

const isLocalFrontend = ['localhost', '127.0.0.1'].includes(window.location.hostname);
const localApiToken = process.env.REACT_APP_TOKEN_LOCAL || '';
const getLocalTokenHeader = () => {
    if (isLocalFrontend && localApiToken) {
        return { 'X-Local-Token': localApiToken };
    }
    return {};
};

if (isLocalFrontend && localApiToken) {
    axios.defaults.headers.common['X-Local-Token'] = localApiToken;
}

// Token do Firebase para a API (o Firebase renova sozinho a cada 1h).
// Falha ao obter o token nunca impede a chamada: a API ainda está em modo observação.
const getFirebaseAuthHeader = async () => {
    try {
        await auth.authStateReady();
        const token = await auth.currentUser?.getIdToken();
        return token ? { Authorization: `Bearer ${token}` } : {};
    } catch (error) {
        return {};
    }
};

// Registrado no carregamento do módulo para cobrir também as chamadas feitas antes do primeiro render
const apiBaseUrl = process.env.REACT_APP_URL_API || 'https://return-navy.vercel.app/';
axios.interceptors.request.use(async (config) => {
    if (config.url?.startsWith(apiBaseUrl)) {
        const { Authorization } = await getFirebaseAuthHeader();
        if (Authorization) {
            config.headers.Authorization = Authorization;
        }
    }
    return config;
});

export function AuthProvider({ children }) {
    const [possuiUnidade, setPossuiUnidade] = useState(true);
    const [errorMessage, setErrorMessage] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingGetUnidadeFuncionarioId, setIsLoadingGetUnidadeFuncionarioId] = useState(false);
    const [isLoadingGetUnidadeId, setIsLoadingGetUnidadeId] = useState(false);

    const [userAcesso, setUserAcesso] = useState(() => {
        return Cookies.get('userAcesso') === 'true';
    });
    const [userInfo, setUserInfo] = useState(() => {
        const storedUserInfo = Cookies.get('userInfo') || undefined;
        return storedUserInfo
            ? { auth: JSON.parse(storedUserInfo) }
            : undefined;
    });
    const [tokenAcess, setTokenAcess] = useState(() => {
        return Cookies.get('tokenAcess') || undefined;
    });
    const urlApi = process.env.REACT_APP_URL_API || 'https://return-navy.vercel.app/';
    const isSecure = window.location.protocol === 'https:';

    const verificarAcesso = useCallback(async (userEmail) => {
        // console.log('verificando acesso para: ', userEmail);
        const userData = {}; // Moved userData initialization here
        try {
            const response = await fetch(`${urlApi}v1/verifica-user/${userEmail}`, {
                headers: {
                    ...(await getFirebaseAuthHeader()),
                    ...getLocalTokenHeader(),
                }
            });
            if (!response.ok) {
                const responseText = await response.text();
                let responseMessage = responseText;
                try {
                    const parsedResponse = JSON.parse(responseText);
                    responseMessage = parsedResponse?.message || responseText;
                } catch (_) {
                    // Mantém o texto bruto caso não seja JSON
                }
                const requestError = new Error(responseMessage || `HTTP ${response.status}`);
                requestError.status = response.status;
                throw requestError;
            }
            const data = await response.json();
            userData.bitrix = data;
            if (data.ACTIVE_TO) {
                const [datePart, timePart] = data.ACTIVE_TO.split(" ");
                const dataFormatada = new Date(datePart.split("/").reverse().join("-") + "T" + timePart);
                if (dataFormatada < new Date()) {
                    setIsLoading(false);
                    setErrorMessage('Acesso negado - Vencimento do acesso');
                    return;
                }
            }
            const property1000 = data.PROPERTY_1000?.[Object.keys(data.PROPERTY_1000)[0]];
            const property948 = data.PROPERTY_948?.[Object.keys(data.PROPERTY_948)[0]];

            if ((!property1000 && data.PERFIL === 'Pregador') || (!property948 && data.PERFIL === 'Diácono')) {
                setErrorMessage('Acesso negado - Seu perfil está incompleto\nConsulte o setor responsável');
                setIsLoading(false);
                return;
            }
            setUserInfo(prev => ({
                ...prev,
                bitrix: {
                    ID: data.ID,
                    PERFIL: data.PERFIL,
                    ACTIVE_TO: data.ACTIVE_TO,
                    ...(data.PROPERTY_1002 && { unidadesLista: Object.values(data.PROPERTY_1002) }),
                    ...(data.PROPERTY_1000 && { PROPERTY_1000: Object.values(data.PROPERTY_1000)[0] }),
                    ...(data.PROPERTY_948 && { PROPERTY_948: Object.values(data.PROPERTY_948)[0] }),
                    ...(data.PROPERTY_998 && { PROPERTY_998: Object.values(data.PROPERTY_998)[0] }),
                }
            }));
            setUserAcesso(true);
            setIsLoading(false);
        } catch (erro) {
            const statusCode = erro?.status;
            const errorMessageText = String(erro?.message || '');
            const isNetworkOrCorsError =
                errorMessageText.includes('NetworkError') ||
                errorMessageText.includes('Failed to fetch') ||
                errorMessageText.includes('Load failed');

            setUserAcesso(false);
            setIsLoading(false);

            if (statusCode === 401) {
                setErrorMessage('Erro 401 - Sessão inválida ou expirada\nFaça login novamente');
                return;
            }
            if (statusCode === 403) {
                setErrorMessage('Erro 403 - Acesso negado\nConsulte o setor responsável');
                return;
            }
            if (statusCode === 404) {
                setErrorMessage('Usuário não encontrado ou sem cadastro de acesso\nConsulte o setor responsável');
                return;
            }
            if (statusCode >= 500) {
                setErrorMessage('Erro interno na API de acesso\nTente novamente em instantes');
                return;
            }
            if (isNetworkOrCorsError) {
                setErrorMessage('Falha de rede/CORS ao validar acesso\nVerifique conexão e configuração da API');
                return;
            }

            setErrorMessage('Não foi possível validar o acesso no momento\nTente novamente em instantes');
        }
    }, [urlApi]);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            if (user) {
                // Contas Microsoft (Entra ID) podem vir com user.email nulo; o email fica no providerData
                const email = user.email || user.providerData?.find(p => p?.email)?.email;
                if (!email) {
                    setErrorMessage('Não foi possível obter o email do usuário.');
                    setIsLoading(false);
                    await signOut(auth);
                    return;
                }
                const fallbackName = email.split('@')[0].replace(/[._]/g, ' ');
                const givenName = user.displayName || fallbackName || email;

                setUserInfo(prev => ({
                    ...prev,
                    auth: {
                        mail: email,
                        givenName,
                    },
                }));

                await verificarAcesso(email);
            } else {
                Object.keys(Cookies.get()).forEach(cookieName => {
                    Cookies.remove(cookieName);
                });
                localStorage.clear();
                delete axios.defaults.headers.common['Authorization'];
                setUserAcesso(false);
                setUserInfo(undefined);
                setTokenAcess(undefined);
            }
        });

        return () => unsubscribe();
    }, [verificarAcesso]);

    useEffect(() => { // Interceptor global: detecta 401 e limpa a sessão
        const interceptorId = axios.interceptors.response.use(
            response => response,
            error => {
                if (error?.response?.status === 401) {
                    delete axios.defaults.headers.common['Authorization'];
                }
                return Promise.reject(error);
            }
        );
        return () => axios.interceptors.response.eject(interceptorId);
    }, []);

    useEffect(() => { // Salva os cookies de sessão quando todos os dados estão prontos
        const tempoExpires = 7;
        if (userAcesso && userInfo?.auth && tokenAcess) {
            Cookies.set('userAcesso', userAcesso, { expires: tempoExpires, sameSite: 'Lax', secure: isSecure });
            Cookies.set('userInfo', JSON.stringify(userInfo.auth), { expires: tempoExpires, sameSite: 'Lax', secure: isSecure });
            Cookies.set('tokenAcess', tokenAcess, { expires: tempoExpires, sameSite: 'Lax', secure: isSecure });
        } else if (!userAcesso && !userInfo?.auth) {
            // Só limpa tudo quando realmente não há sessão (logout ou sessão inválida)
            Object.keys(Cookies.get()).forEach(cookieName => { Cookies.remove(cookieName); });
            localStorage.clear();
            delete axios.defaults.headers.common['Authorization'];
        }
    }, [userAcesso, userInfo?.auth, tokenAcess, isSecure]);

    useEffect(() => {
        const getUnidadeFuncionarioId = async () => {
            if (userInfo?.bitrix?.PROPERTY_1000 && !isLoadingGetUnidadeFuncionarioId) {
                try {
                    setIsLoadingGetUnidadeFuncionarioId(true);
                    const funcionarioId = userInfo?.bitrix?.PROPERTY_1000;
                    const response = await axios.get(`${urlApi}v1/unidadeFuncionario?idFuncionario=${funcionarioId}`);
                    const unidadeIdConst = response.data.results.departamento;

                    const unidadesLista = userInfo?.bitrix?.unidadesLista || [];
                    let responseNomes;
                    if (unidadesLista && unidadesLista.length > 0) {
                        responseNomes = await axios.get(`${urlApi}v1/unidades_nomes_array?ids=${unidadesLista.join(',')}`);
                    }

                    setUserInfo((prevUserInfo) => ({
                        ...prevUserInfo,
                        bitrix: {
                            ...prevUserInfo.bitrix,
                            unidadeId: unidadeIdConst,
                            ...(responseNomes && { unidades: responseNomes.data.results }),
                        },
                    }));

                } catch (erro) {
                    console.error(erro)
                    console.error("Erro ao obter unidade do funcionário - AuthContext");
                } finally {
                    setIsLoadingGetUnidadeFuncionarioId(false);
                }
            }
        };

        if (userAcesso && userInfo && userInfo.bitrix) {
            if (userInfo.bitrix.PERFIL === 'Pregador') {
                if (
                    !userInfo?.bitrix?.unidades &&
                    !userInfo.bitrix.acessoTotal
                ) {
                    // console.log("userInfo: ",userInfo);
                    getUnidadeFuncionarioId();
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userInfo?.bitrix?.PROPERTY_1000, userInfo?.bitrix?.unidades, userInfo?.bitrix?.acessoTotal
        // , userInfo, userAcesso, urlApi
    ]);

    useEffect(() => {
        const getUnidadeId = async () => {
            if (userInfo?.bitrix?.PROPERTY_948 && !isLoadingGetUnidadeId) {
                try {
                    setIsLoadingGetUnidadeId(true);

                    const unidadesLista = userInfo?.bitrix?.unidadesLista || [];
                    const response = await axios.get(`${urlApi}v1/unidades_nomes_array?ids=${unidadesLista.join(',')}`);

                    setUserInfo((prevUserInfo) => ({
                        ...prevUserInfo,
                        bitrix: {
                            ...prevUserInfo.bitrix,
                            acessoTotal: false,
                            unidades: response.data.results
                        },
                    }));
                } catch (erro) {
                    console.error("Erro ao obter unidade do contato - AuthContext");
                    setPossuiUnidade(false);
                } finally {
                    setIsLoadingGetUnidadeId(false);
                }
            }
        };
        if (userAcesso && userInfo && userInfo.bitrix) {
            if (userInfo.bitrix.PERFIL === 'Diácono') {
                if (
                    !userInfo?.bitrix?.unidades
                ) {
                    getUnidadeId();
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userInfo?.bitrix?.PROPERTY_948]);

    useEffect(() => {
        const acessoTotalExiste = userInfo?.bitrix?.acessoTotal !== undefined;
        // console.log("acessoTotalExiste", acessoTotalExiste)
        if (acessoTotalExiste) {
            // console.log("avaliado")
            if (
                userInfo?.bitrix?.unidades?.length > 0 ||
                userInfo?.bitrix?.acessoTotal
            ) {
                setPossuiUnidade(true);
            } else {
                setPossuiUnidade(false);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userInfo?.bitrix?.acessoTotal]);

    useEffect(() => { // Define quem tem acesso total
        if (userInfo?.bitrix?.unidadeId && userInfo?.bitrix?.PERFIL === 'Pregador') {
            const seeAllUnitsId = {
                "Religião de Deus": { "id": 1 },
                "Religião Divina": { "id": 113 },
                "STI - Sistemas de Marketing e Comunicação": { "id": 1021 },
                "MACROREGIÃO - APOSTOLAR (João Carlos)": { "id": 35 },
                "Assessoria de Relacionamento": { "id": 1025 },
                "MINISTÉRIO DIGITAL": { "id": 1027 },
                "MINISTÉRIO DIGITAL - GESTÃO": { "id": 1156 },
                "RIO DE JANEIRO (Região)": { "id": 1192 },
            };
            const temAcessoTotal = userInfo.bitrix.unidadeId.some(id =>
                Object.values(seeAllUnitsId).some(unit => unit.id === Number(id))
            );
            setUserInfo(prevUserInfo => ({
                ...prevUserInfo,
                bitrix: {
                    ...prevUserInfo.bitrix,
                    /*
                    acessoTotal: false,
                    */
                   acessoTotal: temAcessoTotal,
                }
            }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userInfo?.bitrix?.unidadeId]);

    const criarToken = useCallback(async (text) => {
        try {
            const response = await fetch(`${urlApi}v1/hashing`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    ...(await getFirebaseAuthHeader()),
                    ...getLocalTokenHeader(),
                },
                body: JSON.stringify({ text })
            });
            // console.log('chamando hashing')

            if (!response.ok) {
                throw new Error("Erro ao criptografar o texto");
            }

            const data = await response.json();
            return data.hash;
        } catch (error) {
            console.error("Erro:", error.message);
            return null;
        }
    }, [urlApi]);

    useEffect(() => { // Gera o tokenAcess com 1 chamada só; se o salvo no cookie for diferente, reinicia a sessão
        const verificarTokenAcess = async () => {
            const tokenSalvo = tokenAcess;
            const encrypted = await criarToken(userInfo.auth.mail);
            if (encrypted && tokenSalvo && encrypted !== tokenSalvo) {
                Object.keys(Cookies.get()).forEach(cookieName => { Cookies.remove(cookieName); });
                localStorage.clear();
                delete axios.defaults.headers.common['Authorization'];
                window.location.href = '/';
                return;
            }
            setTokenAcess(encrypted);
        };
        if (userInfo?.auth?.mail) {
            verificarTokenAcess();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [criarToken, userInfo?.auth?.mail]);

    // verificarAcesso é chamado pelo onAuthStateChanged, que sempre dispara ao abrir o app

    // useEffect(() => {
    //     console.log('userInfo: ', userInfo)
    // }, [userInfo])

    return (
        <AuthContext.Provider value={{
            userAcesso,
            setUserAcesso,
            userInfo,
            setUserInfo,
            possuiUnidade,
            verificarAcesso,
            isLoading,
            setIsLoading,
            errorMessage,
            setErrorMessage
        }}>
            {children}
        </AuthContext.Provider>
    );
}
